import {
  collection,
  doc,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";

const parseMessageDate = (val) => {
  if (!val) return 0;
  if (typeof val.toDate === "function") {
    try {
      return val.toDate().getTime();
    } catch {
      return 0;
    }
  }
  if (val instanceof Date) return val.getTime();
  if (typeof val === "number") return val;
  if (typeof val === "string") {
    const d = new Date(val);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  }
  if (val.seconds) return val.seconds * 1000;
  return 0;
};

class FirebaseChatService {
  subscribeToAllChats(callback, onError) {
    const q = query(collection(db, "chats"), orderBy("lastMessageAt", "desc"));
    return onSnapshot(
      q,
      (snapshot) => {
        const chats = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        callback(chats);
      },
      (error) => {
        console.error("Firestore subscribeToAllChats error:", error);
        if (onError) onError(error);
      },
    );
  }

  subscribeToChatMessages(chatId, callback, onError) {
    if (!chatId) return () => {};

    const messagesCol = collection(db, "chats", chatId, "messages");
    const q = query(messagesCol, orderBy("createdAt", "asc"));

    let isFallback = false;
    let fallbackUnsub = null;

    const handleSnapshot = (snapshot) => {
      const messages = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      messages.sort((a, b) => parseMessageDate(a.createdAt) - parseMessageDate(b.createdAt));
      callback(messages);
    };

    const primaryUnsub = onSnapshot(
      q,
      handleSnapshot,
      (error) => {
        console.warn("Firestore ordered messages query failed, attempting unordered fallback:", error.message || error);
        if (!isFallback) {
          isFallback = true;
          try {
            fallbackUnsub = onSnapshot(
              messagesCol,
              handleSnapshot,
              (fallbackError) => {
                console.error("Firestore subscribeToChatMessages error:", fallbackError);
                if (onError) onError(fallbackError);
              },
            );
          } catch (e) {
            console.error("Failed to start fallback subscription:", e);
            if (onError) onError(error);
          }
        } else {
          if (onError) onError(error);
        }
      },
    );

    return () => {
      if (primaryUnsub) primaryUnsub();
      if (fallbackUnsub) fallbackUnsub();
    };
  }

  async sendMessage(chatId, text, senderEmail, senderRole = "admin") {
    if (!text.trim() || !chatId) return;
    const chatRef = doc(db, "chats", chatId);
    const messagesRef = collection(db, "chats", chatId, "messages");

    await addDoc(messagesRef, {
      text,
      sender: senderRole,
      email: senderEmail,
      createdAt: serverTimestamp(),
    });

    await setDoc(
      chatRef,
      {
        lastMessage: text,
        lastMessageAt: serverTimestamp(),
        lastMessageSender: senderRole,
        email: chatId,
        ...(senderRole === "support" && { adminLastRead: serverTimestamp() }),
      },
      { merge: true },
    );
  }

  async markAsReadByAdmin(chatId) {
    if (!chatId) return;
    try {
      const chatRef = doc(db, "chats", chatId);
      await updateDoc(chatRef, {
        adminLastRead: serverTimestamp(),
      });
    } catch (e) {
      console.warn("Failed to mark chat as read:", e);
    }
  }
}

export default new FirebaseChatService();
